import { getCookie, setCookie } from 'cookies-next'
import { jwtDecode } from 'jwt-decode'
import { renewAccessToken } from './auth/auth'

export const baseURL = process.env.NEXT_PUBLIC_ADMIN_API_URL

export async function getHeader(): Promise<{ Authorization: string; Accept: string } | undefined> {
  try {
    const accessToken = await getAccessToken()
    if (!accessToken) {
      return Promise.resolve(undefined)
    }
    return { Authorization: `Bearer ${accessToken}`, Accept: 'application/json' }
  } catch (err) {
    return Promise.reject(err)
  }
}

export async function getAccessToken(): Promise<string | null> {
  try {
    if (typeof window === 'undefined') {
      // server - access token renewed in the middleware
      const { cookies } = await import('next/headers')
      const accessToken = await getCookie('admin_access_token', { cookies })
      return Promise.resolve(accessToken || null)
    }

    // client
    let accessToken = getCookie('admin_access_token') as string
    // Buffer of 30 seconds to prevent race conditions during request flight
    const isExpired = accessToken ? getTokenExp(accessToken).getTime() <= Date.now() + 30 * 1000 : false

    if (!accessToken || isExpired) {
      // renew access token
      const refreshToken = getCookie('admin_refresh_token') as string
      if (!refreshToken) {
        return Promise.resolve(null)
      }
      const result = await renewAccessToken(refreshToken)
      accessToken = result?.access_token
      if (accessToken) {
        setCookie('admin_access_token', accessToken, { expires: getTokenExp(accessToken) })
      }
      const idToken = result?.id_token
      if (idToken) {
        setCookie('admin_id_token', idToken, { expires: getTokenExp(idToken) })
      }
    }
    return Promise.resolve(accessToken)
  } catch (err) {
    return Promise.reject(err)
  }
}

function getTokenExp(token: string): Date {
  try {
    const decoded = jwtDecode(token)
    if (!decoded.exp) {
      return new Date(0)
    }
    return new Date(decoded.exp * 1000)
  } catch {
    return new Date(0)
  }
}

export function formatFetchError(err: unknown) {
  const error = err as {
    status?: number
    message?: string
    response?: { data?: { message?: string } }
  }

  const x = {
    status: error.status || 500,
    message: error.response?.data?.message || error.message || 'Unknown error',
  }
  return x
}
